import * as THREE from "three";
import { computeKinematics } from "./forwardKinematics";
import type { IkChain, IkOptions, IkResult, IkTarget } from "./types";

const clamp = (v: number, min?: number, max?: number) => {
  if (min !== undefined && v < min) return min;
  if (max !== undefined && v > max) return max;
  return v;
};

function invertSquare(m: number[][]): number[][] | null {
  const n = m.length;
  if (n === 0 || m.some((row) => row.length !== n)) return null;

  // Gauss-Jordan inversion (n is small: 3 or 6).
  const a = m.map((row) => row.slice());
  const inv: number[][] = Array.from({ length: n }, (_, r) =>
    Array.from({ length: n }, (_, c) => (r === c ? 1 : 0))
  );

  for (let col = 0; col < n; col++) {
    // Pivot
    let pivotRow = col;
    let pivotAbs = Math.abs(a[col][col] ?? 0);
    for (let r = col + 1; r < n; r++) {
      const v = Math.abs(a[r][col] ?? 0);
      if (v > pivotAbs) {
        pivotAbs = v;
        pivotRow = r;
      }
    }
    if (pivotAbs < 1e-12) return null;

    if (pivotRow !== col) {
      const tmp = a[col];
      a[col] = a[pivotRow];
      a[pivotRow] = tmp;
      const tmpInv = inv[col];
      inv[col] = inv[pivotRow];
      inv[pivotRow] = tmpInv;
    }

    // Normalize pivot row
    const pivot = a[col][col];
    const invPivot = 1 / pivot;
    for (let c = 0; c < n; c++) {
      a[col][c] *= invPivot;
      inv[col][c] *= invPivot;
    }

    // Eliminate other rows
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = a[r][col];
      if (Math.abs(factor) < 1e-15) continue;
      for (let c = 0; c < n; c++) {
        a[r][c] -= factor * a[col][c];
        inv[r][c] -= factor * inv[col][c];
      }
    }
  }

  return inv;
}

function dampedLeastSquares(J: number[][], error: number[], lambda: number): number[] | null {
  const m = J.length; // rows (3 or 6)
  const n = J[0]?.length || 0;
  if (m === 0 || n === 0 || error.length !== m) return null;

  // Compute JJ^T (m x m)
  const JT = Array.from({ length: n }, (_, col) => J.map((row) => row[col]));
  const JJt: number[][] = Array.from({ length: m }, () => Array(m).fill(0));
  for (let r = 0; r < m; r++) {
    for (let c = 0; c < m; c++) {
      let sum = 0;
      for (let k = 0; k < n; k++) sum += J[r][k] * J[c][k];
      JJt[r][c] = sum + (r === c ? lambda * lambda : 0);
    }
  }

  const inv = invertSquare(JJt);
  if (!inv) return null;

  // inv * error (m)
  const invErr = inv.map((row) => {
    let sum = 0;
    for (let i = 0; i < m; i++) sum += row[i] * error[i];
    return sum;
  });

  // J^T * (invErr) -> (n)
  const delta = Array(n).fill(0);
  for (let k = 0; k < n; k++) {
    for (let r = 0; r < m; r++) {
      delta[k] += JT[k][r] * invErr[r];
    }
  }

  return delta;
}

function quatFromTarget(q?: number[]): THREE.Quaternion | null {
  if (!Array.isArray(q) || q.length < 4) return null;
  // Stored as [w, x, y, z]
  return new THREE.Quaternion(q[1], q[2], q[3], q[0]).normalize();
}

function rotationErrorVector(current: THREE.Quaternion, target: THREE.Quaternion): THREE.Vector3 {
  // q_err = q_target * q_current^-1
  const qErr = target.clone().multiply(current.clone().invert()).normalize();
  // Ensure shortest path
  if (qErr.w < 0) {
    qErr.x *= -1;
    qErr.y *= -1;
    qErr.z *= -1;
    qErr.w *= -1;
  }

  const w = clamp(qErr.w, -1, 1);
  let angle = 2 * Math.acos(w);
  if (angle > Math.PI) angle -= 2 * Math.PI;
  const s = Math.sqrt(Math.max(1e-16, 1 - w * w)); // ~ sin(angle/2)
  if (s < 1e-8 || !isFinite(angle)) return new THREE.Vector3(0, 0, 0);
  const axis = new THREE.Vector3(qErr.x / s, qErr.y / s, qErr.z / s).normalize();
  return axis.multiplyScalar(angle);
}

export function solvePositionIk(
  chain: IkChain,
  target: IkTarget,
  initial: number[],
  options: IkOptions = {}
): IkResult {
  const maxIterations = options.maxIterations ?? 32;
  const tolerance = options.tolerance ?? 1e-3;
  const rotationTolerance = options.rotationTolerance ?? 2e-2;
  const damping = options.damping ?? 0.01;
  const stepSize = options.stepSize ?? 1.0;
  const orientationWeight = options.orientationWeight ?? 0.25;

  let values = [...initial];
  let iterations = 0;
  let lastError = Infinity;
  const targetQuat = quatFromTarget(target.orientation);

  for (; iterations < maxIterations; iterations++) {
    const kin = computeKinematics(chain, values);
    const currentPos = kin.endEffector.position;
    const targetVec = new THREE.Vector3(
      target.position[0] ?? 0,
      target.position[1] ?? 0,
      target.position[2] ?? 0
    );
    const errorVec = targetVec.clone().sub(currentPos);
    const errNorm = errorVec.length();
    const rotErrVec =
      targetQuat && kin.endEffector.rotation instanceof THREE.Quaternion
        ? rotationErrorVector(kin.endEffector.rotation, targetQuat)
        : new THREE.Vector3(0, 0, 0);
    const rotErrNorm = rotErrVec.length();

    // Keep `error` backward-compatible: it remains a position error.
    lastError = errNorm;

    if (errNorm < tolerance && (!targetQuat || rotErrNorm < rotationTolerance)) {
      return { status: "success", iterations, error: errNorm, jointValues: values };
    }

    const n = chain.dofCount || 0;
    const wantsOrientation = !!targetQuat;
    const rows = wantsOrientation ? 6 : 3;
    const J: number[][] = Array.from({ length: rows }, () => Array(n).fill(0));
    kin.linkStates.forEach((state, linkIdx) => {
      const link = chain.links[linkIdx];
      const di = link?.dofIndex;
      if (di === undefined || di === null) return;
      const r = currentPos.clone().sub(state.anchorWorld);
      if (link.kind === "hinge") {
        const axis = state.axisWorld;
        const jac = axis.clone().cross(r);
        J[0][di] = jac.x;
        J[1][di] = jac.y;
        J[2][di] = jac.z;
        if (wantsOrientation) {
          J[3][di] = axis.x * orientationWeight;
          J[4][di] = axis.y * orientationWeight;
          J[5][di] = axis.z * orientationWeight;
        }
      } else {
        const axis = state.axisWorld;
        J[0][di] = axis.x;
        J[1][di] = axis.y;
        J[2][di] = axis.z;
        if (wantsOrientation) {
          J[3][di] = 0;
          J[4][di] = 0;
          J[5][di] = 0;
        }
      }
    });

    const errArr = wantsOrientation
      ? [
          errorVec.x,
          errorVec.y,
          errorVec.z,
          rotErrVec.x * orientationWeight,
          rotErrVec.y * orientationWeight,
          rotErrVec.z * orientationWeight,
        ]
      : [errorVec.x, errorVec.y, errorVec.z];

    const delta = dampedLeastSquares(J, errArr, damping);
    if (!delta) break;

    values = values.map((v, idx) => {
      const step = clamp(delta[idx] * stepSize, -0.25, 0.25); // avoid huge jumps
      const proposed = v + step;
      const link = chain.links.find((l) => l.dofIndex === idx) || null;
      const limit = link?.limit;
      return clamp(proposed, limit?.[0], limit?.[1]);
    });
  }

  return {
    status: iterations >= maxIterations ? "max_iterations" : "failed",
    iterations,
    error: lastError,
    jointValues: values,
  };
}
